import { useQuery } from "@tanstack/react-query";
import type { JSONContent } from "@tiptap/react";
import { useCallback, useMemo } from "react";

import { repairNestedMarkdownImageLinks } from "@/shared/components/editor/lib/markdown-image";

import * as mineApi from "../api";
import {
  getTextContentMode,
  hydrateAttachmentUrls,
  parseTiptapJson,
  type TextContentMode,
  type TextEditorContent,
} from "../text-content";
import type { Item } from "../types";

type TextPreviewContentQueryResult = {
  content: TextEditorContent;
};

export type TextPreviewContentState = {
  content: TextEditorContent;
  contentMode: TextContentMode;
  errorMessage: string | null;
  isLoading: boolean;
  isRefreshing: boolean;
  refresh: () => Promise<void>;
};

function textToEditorJson(text: string): JSONContent {
  const paragraphs = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    type: "doc",
    content: paragraphs.length > 0
      ? paragraphs.map((paragraph) => ({
          type: "paragraph",
          content: [{ type: "text", text: paragraph }],
        }))
      : [{ type: "paragraph" }],
  };
}

function getFallbackContent(item: Item, mode: TextContentMode): TextEditorContent {
  if (mode === "markdown") {
    return item.note ?? "";
  }

  try {
    return parseTiptapJson(item.note || "");
  } catch {
    return textToEditorJson(item.note || "");
  }
}

async function loadTextContent(item: Item, mode: TextContentMode) {
  const rawContentResponse = await fetch(item.url ?? "");

  if (!rawContentResponse.ok) {
    throw new Error("文本内容加载失败");
  }

  const rawContent = await rawContentResponse.text();

  if (mode === "markdown") {
    return repairNestedMarkdownImageLinks(rawContent);
  }

  const attachmentsResponse = await mineApi.getItemAttachments(item.id);
  const attachmentUrlMap = new Map(
    attachmentsResponse.attachments.map((attachment) => [
      attachment.id,
      attachment.download_url,
    ]),
  );

  return hydrateAttachmentUrls(parseTiptapJson(rawContent), attachmentUrlMap);
}

export function useTextPreviewContent(item?: Item): TextPreviewContentState {
  const contentModeResult = useMemo(() => {
    try {
      return {
        error: null,
        mode: getTextContentMode(item?.mime_type),
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : "不支持的文本内容类型",
        mode: "tiptap-json" as const,
      };
    }
  }, [item?.mime_type]);
  const contentMode = contentModeResult.mode;
  const contentQuery = useQuery<TextPreviewContentQueryResult>({
    enabled: !!item?.url && !contentModeResult.error,
    queryKey: [
      "mine",
      "items",
      item?.id ?? null,
      "text-content",
      contentMode,
    ],
    queryFn: async () => {
      if (!item) {
        throw new Error("缺少文本收藏信息");
      }

      return {
        content: await loadTextContent(item, contentMode),
      };
    },
  });
  const refresh = useCallback(async () => {
    if (!item?.url || contentModeResult.error) {
      return;
    }

    await contentQuery.refetch();
  }, [contentModeResult.error, contentQuery, item?.url]);
  const fallbackContent = item
    ? getFallbackContent(item, contentMode)
    : textToEditorJson("");

  return {
    content: item?.url
      ? contentQuery.data?.content ?? fallbackContent
      : fallbackContent,
    contentMode,
    errorMessage: contentModeResult.error ?? (contentQuery.isError
      ? "文本内容加载失败，已展示摘要内容"
      : null),
    isLoading: !!item?.url && contentQuery.isLoading,
    isRefreshing: !!item?.url && contentQuery.isFetching,
    refresh,
  };
}
